// What the signed-in person pays Greenback for. See billingAccountsStore for
// when it is asked and why it is a module store.

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BillingAccount, groupAccountKey } from '../services/billingAccountsService';
import {
  BillingAccountsState,
  ensureBillingAccountsLoaded,
  getBillingAccountsState,
  refreshBillingAccountsIfStale,
  subscribeBillingAccounts,
} from '../services/billingAccountsStore';

/** Follows the store without asking it anything. */
export function useBillingAccountsState(): BillingAccountsState {
  const [current, setCurrent] = useState<BillingAccountsState>(getBillingAccountsState);

  useEffect(() => {
    const unsubscribe = subscribeBillingAccounts(setCurrent);
    // A change between the first render and this effect would otherwise be missed.
    setCurrent(getBillingAccountsState());
    return unsubscribe;
  }, []);

  return current;
}

/**
 * Asks for the list once per sign-in, and again on window focus once five
 * minutes have passed. For the shell's badge and for /subscription: the
 * screens that are about the list. Anything else reads it with
 * useManagedBillingAccount, which never asks.
 */
export function useBillingAccounts(): BillingAccountsState {
  const { user } = useAuth();
  const uuid = user?.authToken ? user.uuid : undefined;
  const current = useBillingAccountsState();

  useEffect(() => {
    if (!uuid) return undefined;
    ensureBillingAccountsLoaded(uuid);
    const onFocus = () => refreshBillingAccountsIfStale();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [uuid]);

  // Somebody else's list never answers for this person, even for the one
  // render before the effect above resets the store.
  return current.uuid === uuid ? current : { uuid, status: 'idle', data: null };
}

/**
 * The account with this key, when the list has arrived and says the person
 * manages it; otherwise null, and the caller keeps today's behaviour. Never
 * asks: the shell's badge already has, so a screen inside the shell reads
 * what that brought back. Null while the list is still coming, which is the
 * same as an api that does not serve it yet.
 */
export function useManagedBillingAccount(key: string | null | undefined): BillingAccount | null {
  const { data } = useBillingAccountsState();
  if (!key || !data) return null;
  return data.accounts.find(account => account.key === key && account.can_manage) ?? null;
}

/**
 * A Business group the person manages whose bill Manage subscription can
 * open, through its Stripe portal (the list's `stripe_portal` action); null
 * otherwise. Without that action (no Stripe customer, or a Stripe row with no
 * subscription, as on QA once test mode drops a mirrored customer) the page
 * offers the group no Manage billing, only a way back to its billing card: a
 * button sending the owner there would bounce them between the two pages, so
 * it keeps its old target, whose own error says what is wrong.
 */
export function useManagedGroupBilling(entityGroupId: string | null | undefined): BillingAccount | null {
  const account = useManagedBillingAccount(entityGroupId ? groupAccountKey(entityGroupId) : null);
  return account?.actions.includes('stripe_portal') ? account : null;
}
